import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts'
import { Users, UserCheck, UserX, Send, Ban, Mail } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader, StatCard, StatCardGroup } from '@/components/shared'
import { emailDashboardService } from '@/services/emailDashboardService'

const METRIC_COLORS = {
  delivered: '#2a78d6',
  opened: '#1baf7a',
  clicked: '#eda100',
  bounced: '#e34948',
}

// Consent-status "health" states, not an arbitrary categorical series — green/red/gray
// mirror the good/critical/neutral meaning, and each slice is always paired with a
// visible numeric label in the legend below (never color alone).
const HEALTH_COLORS = ['#1baf7a', '#e34948', '#c3c2b7']

const Dashboard = () => {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery('email-dashboard-summary', emailDashboardService.getSummary)
  const summary = data?.data

  const trend = (summary?.trend || []).map((row) => ({
    ...row,
    label: new Date(row.period).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  }))

  const contactHealth = summary?.contactHealth
  const contactHealthData = contactHealth
    ? [
        { name: 'Subscribed', value: contactHealth.subscribed },
        { name: 'Unsubscribed', value: contactHealth.unsubscribed },
        { name: 'Pending', value: contactHealth.pending },
      ]
    : []
  const hasContactHealthData = contactHealthData.some((d) => d.value > 0)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Marketing Email"
        description="Overview of your contacts, campaigns and engagement"
        actions={
          <Button size="sm" onClick={() => navigate('/marketing-email/contacts')}>
            Manage Contacts
          </Button>
        }
      />

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <StatCardGroup className="lg:grid-cols-3">
          <StatCard
            title="Total Contacts"
            value={summary?.totalContacts ?? 0}
            icon={Users}
            subDescription={summary?.newThisWeek > 0 ? `+${summary.newThisWeek} new this week` : 'No new contacts this week'}
            contentClassName="p-4"
          />
          <StatCard title="Campaigns Sent" value={summary?.campaignsSent ?? 0} icon={Send} subDescription="lifetime total" contentClassName="p-4" />
          <StatCard
            title="Emails Delivered"
            value={summary?.totalDelivered ?? 0}
            icon={Mail}
            subDescription={summary?.totalDelivered > 0 ? `${summary.avgDeliveryRate}% avg. delivery rate` : 'No deliveries yet'}
            contentClassName="p-4"
          />
          <StatCard
            title="Avg Open Rate"
            value={`${summary?.avgOpenRate ?? 0}%`}
            icon={UserCheck}
            subDescription={summary?.openRateHealth}
            contentClassName="p-4"
          />
          <StatCard
            title="Avg Click Rate"
            value={`${summary?.avgClickRate ?? 0}%`}
            icon={UserX}
            subDescription={summary?.clickRateHealth}
            contentClassName="p-4"
          />
          <StatCard title="Suppression List" value={summary?.suppressionCount ?? 0} icon={Ban} contentClassName="p-4" />
        </StatCardGroup>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Performance Trend (last 12 weeks)</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-72 w-full" />
            ) : trend.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">
                Campaign performance charts will appear here once you start sending campaigns.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="delivered" name="Delivered" stroke={METRIC_COLORS.delivered} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="opened" name="Opened" stroke={METRIC_COLORS.opened} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="clicked" name="Clicked" stroke={METRIC_COLORS.clicked} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="bounced" name="Bounced" stroke={METRIC_COLORS.bounced} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Health</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex flex-col items-center gap-5">
                <Skeleton className="h-[140px] w-[140px] rounded-full" />
                <div className="w-full space-y-2.5">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
                </div>
              </div>
            ) : !hasContactHealthData ? (
              <div className="p-10 text-center text-muted-foreground">No contacts yet.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={contactHealthData}
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={74}
                      dataKey="value"
                      strokeWidth={0}
                      paddingAngle={2}
                    >
                      {contactHealthData.map((_, i) => (
                        <Cell key={i} fill={HEALTH_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="mt-5 divide-y divide-border border-y border-border">
                  {contactHealthData.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: HEALTH_COLORS[i] }} />
                        <span className="text-sm text-muted-foreground">{d.name}</span>
                      </div>
                      <span className="text-sm font-semibold tabular-nums">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Dashboard
